'use client';
import type { GoogleMaps_Near } from '@/__generated__/graphql';
import {
  useEntrySearchQuery,
  useEntrySearchStore,
  useFilter,
  type Variables,
} from '@liquorice/next-search';
import React from 'react';
import type { PlaceAutocompleteCallback } from './PlaceAutocomplete';

export interface LocationSearchFilterOptions {
  fieldHandle: string;
  stateFieldHandle?: string;
  range?: number;
}

export default function useLocationSearchFilter(options: LocationSearchFilterOptions) {
  const { fieldHandle, stateFieldHandle, range = 50 } = options;

  const [value, setValue] = React.useState<Omit<GoogleMaps_Near, 'range'> | null>(null);
  const [stateValue, setStateValue] = React.useState<string | null>(null);

  const autoSubmit = useEntrySearchStore((s) => s.autoSubmit);
  const { submit } = useEntrySearchQuery();

  const filterCallback = React.useCallback(
    (variables: Variables) => {
      const nextVariables = {
        ...variables,
      } satisfies Variables;

      if (value) {
        const near: GoogleMaps_Near = {
          lat: value.lat,
          lng: value.lng,
          range,
        };
        // [fieldHandle]: near,

        nextVariables[fieldHandle] = near;
      }

      if (stateValue && stateFieldHandle) {
        nextVariables[stateFieldHandle] = stateValue;
      }

      // console.log('nextVariables', nextVariables);
      return nextVariables;
    },
    [fieldHandle, range, stateFieldHandle, stateValue, value],
  );

  useFilter({
    id: 'location',
    callback: filterCallback,
  });

  const handleChange: PlaceAutocompleteCallback = (place) => {
    // console.log('handleChange', place);

    let nextStateValue = place?.location?.state ?? null;
    let nextValue: Omit<GoogleMaps_Near, 'range'> | null = null;

    if (stateFieldHandle && place?.location.state) {
      nextStateValue = place.location.state.toUpperCase();
    } else if (!!place?.center) {
      const { lat, lng } = place?.center;
      nextValue = { lat, lng };
    }

    setValue(nextValue);
    setStateValue(nextStateValue);

    if (autoSubmit) {
      submit();
    }
  };

  return {
    value,
    handleChange,
  };
}
